import { useState, useRef, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import ChatHistory from "./ChatHistory";
import ChatInput from "./ChatInput";
import ChartDisplay from "./ChartDisplay";
import ThinkingText from "./ThinkingText";
import TimeoutNotice from "./TimeoutNotice";
import { usePostHog } from "./PostHogProvider";
import { useApiAuth } from "../hooks/useApiAuth";
import config from "../config";
import "./Chat.css";
import Footer from "./Footer";

const STORAGE_KEY = "chat_wdl_question_count";
const THINKING_MESSAGE_DELAY_MS = 2500; // Minimum delay between thinking message updates in milliseconds

const ChatInterface = ({ isMobileNavOpen, closeMobileNav }) => {
  // New state for managing multiple conversations
  const [conversations, setConversations] = useState([]);
  const [currentConversationIndex, setCurrentConversationIndex] = useState(-1);
  const [conversationId, setConversationId] = useState(null);
  const [timeoutConversationId, setTimeoutConversationId] = useState(null);
  const [timeoutNotice, setTimeoutNotice] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [inputMessage, setInputMessage] = useState(() => {
    // Initialize input message from localStorage if available
    const pendingQuestion = localStorage.getItem("chat_wdl_pending_question");
    if (pendingQuestion) {
      return pendingQuestion;
    }
    return "";
  });
  const [currentCharts, setCurrentCharts] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [typedResponse, setTypedResponse] = useState("");
  const [thinkingMessages, setThinkingMessages] = useState([]);
  const lastThinkingMessageTimeRef = useRef(0);
  const thinkingMessageTimeoutRef = useRef(null);
  const [questionCount, setQuestionCount] = useState(() => {
    const savedCount = localStorage.getItem(STORAGE_KEY);
    return savedCount ? parseInt(savedCount, 10) : 0;
  });
  const messagesEndRef = useRef(null);
  const typingIntervalRef = useRef(null);
  const typingStartTimeRef = useRef(null);
  const typingResponseRef = useRef(null);
  const typingChartsRef = useRef(null);
  const { isAuthenticated, user } = useAuth0();
  const isWdlUser = user?.email?.toLowerCase().endsWith("@welldatalabs.com");
  const { getAuthHeaders } = useApiAuth();
  const { posthog } = usePostHog();
  const navigate = useNavigate();
  const { conversationId: urlConversationId } = useParams();
  const location = useLocation();

  // Load conversations from API on mount
  useEffect(() => {
    const fetchConversationHistory = async () => {
      if (user?.sub) {
        try {
          const authHeaders = await getAuthHeaders();
          const response = await axios.get(`${config.apiUrl}/conversation`, {
            headers: authHeaders,
          });

          // Extract conversation data
          let conversationData = response.data;
          if (response.data.body) {
            conversationData = response.data.body;
          } else if (response.data.data) {
            conversationData = response.data.data;
          }

          // Set conversations from API response
          const fetchedConversations = conversationData.conversations || [];

          // Sort conversations by lastUpdatedUtc (most recent first)
          const sortedConversations = fetchedConversations.sort((a, b) => {
            if (!a.lastUpdatedUtc || !b.lastUpdatedUtc) return 0;
            return new Date(b.lastUpdatedUtc) - new Date(a.lastUpdatedUtc);
          });

          setConversations(sortedConversations);
        } catch (error) {
          console.error("Error loading conversation history:", error);
          setConversations([]);
        }
      }
    };

    fetchConversationHistory();
  }, [user?.sub]);

  // Handle URL conversation ID parameter
  useEffect(() => {
    if (urlConversationId && conversations.length > 0) {
      // Find the conversation index by ID
      const conversationIndex = conversations.findIndex(
        (conv) => conv.conversationId === urlConversationId,
      );

      if (conversationIndex !== -1) {
        // Only load if we don't already have this conversation loaded
        if (conversationId !== urlConversationId) {
          handleConversationClick(conversationIndex);
        }
      } else {
        // Conversation not found, redirect to /chat
        navigate("/chat", { replace: true });
      }
    } else if (urlConversationId && conversations.length === 0) {
      // Conversations not loaded yet, wait for them to load
      return;
    } else if (!urlConversationId && conversationId) {
      // No URL conversation ID but we have one in state, clear it
      setConversationId(null);
      setTimeoutConversationId(null);
      setTimeoutNotice(null);
      setCurrentConversationIndex(-1);
      setMessages([]);
    }
  }, [urlConversationId, conversations, conversationId, navigate]);

  // Remove localStorage saving - conversations stay in state only

  // Update localStorage when questionCount changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, questionCount.toString());
  }, [questionCount]);

  // Check question limit
  useEffect(() => {
    if (
      !isWdlUser &&
      config.limits.maxQuestions !== null &&
      questionCount >= config.limits.maxQuestions
    ) {
      navigate("/contact");
    }
  }, [questionCount, navigate, isWdlUser]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typedResponse, thinkingMessages, timeoutNotice]);

  // Handle page visibility changes to catch up typing when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      // When page becomes visible and we're typing, trigger an immediate update
      if (!document.hidden && isTyping && typingResponseRef.current) {
        // Force an immediate update to catch up
        const elapsed = Date.now() - (typingStartTimeRef.current || Date.now());
        const CHAR_DELAY_MS = 20;
        const targetIndex = Math.min(
          Math.floor(elapsed / CHAR_DELAY_MS),
          typingResponseRef.current.length,
        );

        if (targetIndex < typingResponseRef.current.length) {
          setTypedResponse(
            typingResponseRef.current.substring(0, targetIndex + 1),
          );
        } else {
          // Should have completed, finish it
          setTypedResponse(typingResponseRef.current);
          setIsTyping(false);
          setMessages((prev) => [
            ...prev,
            createMessage(
              typingResponseRef.current,
              "ai",
              typingChartsRef.current || [],
            ),
          ]);

          if (typingIntervalRef.current) {
            clearInterval(typingIntervalRef.current);
            typingIntervalRef.current = null;
          }
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isTyping]);

  // Cleanup typing interval and thinking message timeout on unmount
  useEffect(() => {
    return () => {
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
        typingIntervalRef.current = null;
      }
      if (thinkingMessageTimeoutRef.current) {
        clearTimeout(thinkingMessageTimeoutRef.current);
        thinkingMessageTimeoutRef.current = null;
      }
    };
  }, []);

  const processResponseText = (text) => {
    // Remove only the <thinking> and </thinking> tags, keeping their contents
    let processedText = text.replace(/<\/?thinking>/g, "");

    // Replace 'fracking' with 'fracing' and 'frack' with 'frac'
    processedText = processedText
      .replace(/fracking/gi, "fracing")
      .replace(/frack/gi, "frac");

    return processedText;
  };

  // Helper function to create message with API structure
  const createMessage = (text, type, charts = []) => {
    return {
      text: text,
      type: type,
      charts: charts,
      timestamp: new Date().toISOString(), // Add timestamp for consistency
      id: `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // Unique ID
    };
  };

  // Normalize HTTP status from different error shapes (fetch, axios, custom stream errors).
  const extractHttpStatus = (error) => {
    const statusCandidates = [
      error?.status,
      error?.statusCode,
      error?.response?.status,
    ];

    for (const candidate of statusCandidates) {
      if (typeof candidate === "number" && Number.isFinite(candidate)) {
        return candidate;
      }
      if (typeof candidate === "string") {
        const statusMatch = candidate.match(/\b([1-5]\d{2})\b/);
        if (statusMatch) {
          return parseInt(statusMatch[1], 10);
        }
      }
    }

    const combinedText = `${error?.message || ""} ${error?.statusText || ""} ${error?.response?.statusText || ""}`;
    const fallbackMatch = combinedText.match(/\b([1-5]\d{2})\b/);
    return fallbackMatch ? parseInt(fallbackMatch[1], 10) : null;
  };

  // Stage often starts stream with 200 and fails later, so detect timeout by status + text patterns.
  const isLikelyTimeoutError = (error) => {
    const httpStatus = extractHttpStatus(error);
    const timeoutStatuses = [408, 502, 503, 504, 524];
    const statusText = `${error?.statusText || ""} ${error?.response?.statusText || ""}`.toLowerCase();
    const messageText = `${error?.message || ""} ${error?.response?.data?.message || ""} ${error?.response?.data?.error || ""}`.toLowerCase();
    const combinedText = `${statusText} ${messageText}`;

    const isTimeoutStatus =
      httpStatus !== null && timeoutStatuses.includes(httpStatus);
    const isTimeoutMessage =
      combinedText.includes("err_timed_out") ||
      combinedText.includes("timeout") ||
      combinedText.includes("timed out") ||
      combinedText.includes("gateway timeout") ||
      combinedText.includes("originresponsetimeout") ||
      combinedText.includes("origin response timeout") ||
      error?.name === "TimeoutError" ||
      error?.code === "ETIMEDOUT";
    const isStreamNetworkDrop =
      combinedText.includes("failed to fetch") ||
      combinedText.includes("network error") ||
      combinedText.includes("networkerror when attempting to fetch resource") ||
      combinedText.includes("network request failed") ||
      combinedText.includes("the network connection was lost") ||
      combinedText.includes("load failed");

    return isTimeoutStatus || isTimeoutMessage || isStreamNetworkDrop;
  };

  const handleSubmit = async (message) => {
    if (!message.trim()) return;

    if (
      !isWdlUser &&
      config.limits.maxQuestions !== null &&
      questionCount >= config.limits.maxQuestions
    ) {
      navigate("/contact");
      return;
    }

    // Track question submission
    // if (posthog) {
    //   posthog.capture('question_submitted', {
    //     distinct_id: user?.sub,
    //     email: user?.email,
    //     name: user?.name,
    //     question_length: message.length,
    //     question_count: questionCount + 1,
    //     max_questions: config.limits.maxQuestions,
    //     question_preview: message.substring(0, 100), // First 100 chars for context
    //   });
    // }

    // Increment question count
    setQuestionCount((prev) => prev + 1);

    // Add user message to history
    const userMessage = createMessage(message, "human");
    setMessages((prev) => [...prev, userMessage]);

    // Update states in a single batch
    // Only set current question for new conversations, keep accumulating for existing ones
    if (!conversationId) {
      // New conversation - no need to track current question
    }
    setCurrentCharts([]);
    setIsTyping(false);
    setTypedResponse("");
    setThinkingMessages([]);
    lastThinkingMessageTimeRef.current = 0; // Reset the timer
    // Clear any pending thinking message timeout
    if (thinkingMessageTimeoutRef.current) {
      clearTimeout(thinkingMessageTimeoutRef.current);
      thinkingMessageTimeoutRef.current = null;
    }
    setInputMessage("");
    setIsLoading(true);
    setTimeoutNotice(null); // Reset timeout notice state

    // If the initial stream timed out before conversationId was committed,
    // continue using the in-flight id so follow-up questions stay in the same thread.
    let currentConversationId = conversationId || timeoutConversationId;
    let streamEndpoint = null;

    try {
      const authHeaders = await getAuthHeaders();
      setTimeoutConversationId(conversationId || null);

      // If this is a new conversation, first call /stream to get the conversationId
      if (!currentConversationId) {
        const createResponse = await fetch(
          `${config.apiUrl}/conversationstream`,
          {
            method: "POST",
            headers: {
              ...authHeaders,
              "Content-Type": "application/json",
            },
          },
        );

        if (!createResponse.ok) {
          const statusError = new Error(`HTTP error! status: ${createResponse.status}`);
          statusError.status = createResponse.status;
          throw statusError;
        }

        // Parse the JSON response to get conversationId
        const createData = await createResponse.json();
        currentConversationId = createData.conversationId;

        if (!currentConversationId) {
          throw new Error("Failed to get conversationId from /stream endpoint");
        }

        // Store the in-flight conversation id for timeout refresh on new chats.
        setTimeoutConversationId(currentConversationId);
      }

      // Now call /{conversationId}/stream with the prompt
      const endpoint = `${config.apiUrl}/conversationstream/${currentConversationId}`;
      streamEndpoint = endpoint;

      // Use fetch for streaming
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          question: message,
        }),
      });

      if (!response.ok) {
        const statusError = new Error(`HTTP error! status: ${response.status}`);
        statusError.status = response.status;
        throw statusError;
      }

      // Handle streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finalAnswer = "";
      let newConversationId = currentConversationId;
      let charts = [];
      let title = null;

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        // Decode the chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Parse concatenated JSON objects from buffer
        while (buffer.trim()) {
          let jsonStart = -1;
          let braceCount = 0;
          let inString = false;
          let escapeNext = false;
          let foundComplete = false;

          // Find the start of a JSON object
          for (let i = 0; i < buffer.length; i++) {
            const char = buffer[i];

            if (escapeNext) {
              escapeNext = false;
              continue;
            }

            if (char === "\\") {
              escapeNext = true;
              continue;
            }

            if (char === '"' && !escapeNext) {
              inString = !inString;
              continue;
            }

            if (!inString) {
              if (char === "{") {
                if (braceCount === 0) {
                  jsonStart = i;
                }
                braceCount++;
              } else if (char === "}") {
                braceCount--;
                if (braceCount === 0 && jsonStart !== -1) {
                  // Found a complete JSON object
                  const jsonStr = buffer.substring(jsonStart, i + 1);
                  buffer = buffer.substring(i + 1).trim(); // Remove processed part
                  foundComplete = true;

                  try {
                    const data = JSON.parse(jsonStr);

                    // Process the data based on type: "thinking" | "tool" | "error" | "response"
                    if (data.type === "thinking" || data.type === "tool") {
                      // Ensure we get a string, not an object
                      const message =
                        typeof data.summary === "string"
                          ? data.summary
                          : typeof data.message === "string"
                            ? data.message
                            : typeof data.text === "string"
                              ? data.text
                              : "";
                      if (message && message.trim()) {
                        const cleanMessage = String(message).trim();
                        const now = Date.now();
                        const timeSinceLastMessage =
                          now - lastThinkingMessageTimeRef.current;

                        // If messages are coming too fast (less than THINKING_MESSAGE_DELAY_MS apart),
                        // delay the update slightly to ensure the first one is visible
                        if (
                          timeSinceLastMessage < THINKING_MESSAGE_DELAY_MS &&
                          lastThinkingMessageTimeRef.current > 0
                        ) {
                          // Clear any existing pending timeout
                          if (thinkingMessageTimeoutRef.current) {
                            clearTimeout(thinkingMessageTimeoutRef.current);
                          }
                          thinkingMessageTimeoutRef.current = setTimeout(() => {
                            setThinkingMessages([cleanMessage]);
                            lastThinkingMessageTimeRef.current = Date.now();
                            thinkingMessageTimeoutRef.current = null;
                          }, THINKING_MESSAGE_DELAY_MS - timeSinceLastMessage);
                        } else {
                          // Clear any existing pending timeout
                          if (thinkingMessageTimeoutRef.current) {
                            clearTimeout(thinkingMessageTimeoutRef.current);
                            thinkingMessageTimeoutRef.current = null;
                          }
                          setThinkingMessages([cleanMessage]);
                          lastThinkingMessageTimeRef.current = now;
                        }
                      }
                    } else if (data.type === "response") {
                      // Handle response type - this is the final answer
                      // Clear thinking messages immediately when response arrives
                      if (thinkingMessageTimeoutRef.current) {
                        clearTimeout(thinkingMessageTimeoutRef.current);
                        thinkingMessageTimeoutRef.current = null;
                      }
                      setThinkingMessages([]);

                      if (data.answer) {
                        finalAnswer = data.answer;
                      }
                      if (data.conversationId) {
                        newConversationId = data.conversationId;
                      }
                      if (data.charts) {
                        charts = data.charts;
                        setCurrentCharts(charts);
                      }
                      if (data.title) {
                        title = data.title;
                      }
                    } else if (data.type === "error") {
                      // Convert stream "error" event into a thrown error so outer catch can render timeout UI.
                      const streamError = new Error(
                        data.message || data.error || "Stream error",
                      );
                      streamError.code = data.code;
                      streamError.status = data.status;
                      streamError.statusCode = data.statusCode;
                      streamError.statusText = data.statusText;
                      throw streamError;
                    }

                    // Extract metadata that might be present in any message type
                    if (data.conversationId && data.type !== "response") {
                      newConversationId = data.conversationId;
                    }
                    if (data.charts && data.type !== "response") {
                      charts = data.charts;
                      setCurrentCharts(charts);
                    }
                    if (data.title && data.type !== "response") {
                      title = data.title;
                    }
                    if (data.answer && data.type !== "response") {
                      finalAnswer = data.answer;
                    }
                  } catch (parseError) {
                    // Invalid JSON object in stream chunk, skip it.
                    // Re-throw non-SyntaxError exceptions so stream errors bubble up.
                    if (!(parseError instanceof SyntaxError)) {
                      throw parseError;
                    }
                  }

                  break; // Processed one object, continue to find more
                }
              }
            }
          }

          // If no complete object found in this iteration, break and keep buffer for next chunk
          if (!foundComplete) {
            break;
          }
        }
      }

      // Process any remaining buffer
      if (buffer.trim()) {
        try {
          const data = JSON.parse(buffer);

          // Handle the four possible types: "thinking" | "tool" | "error" | "response"
          if (data.type === "thinking" || data.type === "tool") {
            const message =
              typeof data.summary === "string"
                ? data.summary
                : typeof data.message === "string"
                  ? data.message
                  : typeof data.text === "string"
                    ? data.text
                    : "";
            if (message && message.trim()) {
              const cleanMessage = String(message).trim();
              const now = Date.now();
              const timeSinceLastMessage =
                now - lastThinkingMessageTimeRef.current;

              // If messages are coming too fast (less than THINKING_MESSAGE_DELAY_MS apart),
              // delay the update slightly to ensure the first one is visible
              if (
                timeSinceLastMessage < THINKING_MESSAGE_DELAY_MS &&
                lastThinkingMessageTimeRef.current > 0
              ) {
                // Clear any existing pending timeout
                if (thinkingMessageTimeoutRef.current) {
                  clearTimeout(thinkingMessageTimeoutRef.current);
                }
                thinkingMessageTimeoutRef.current = setTimeout(() => {
                  setThinkingMessages([cleanMessage]);
                  lastThinkingMessageTimeRef.current = Date.now();
                  thinkingMessageTimeoutRef.current = null;
                }, THINKING_MESSAGE_DELAY_MS - timeSinceLastMessage);
              } else {
                // Clear any existing pending timeout
                if (thinkingMessageTimeoutRef.current) {
                  clearTimeout(thinkingMessageTimeoutRef.current);
                  thinkingMessageTimeoutRef.current = null;
                }
                setThinkingMessages([cleanMessage]);
                lastThinkingMessageTimeRef.current = now;
              }
            }
          } else if (data.type === "response") {
            // Handle response type - this is the final answer
            // Clear thinking messages immediately when response arrives
            if (thinkingMessageTimeoutRef.current) {
              clearTimeout(thinkingMessageTimeoutRef.current);
              thinkingMessageTimeoutRef.current = null;
            }
            setThinkingMessages([]);

            if (data.answer) {
              finalAnswer = data.answer;
            }
            if (data.conversationId) {
              newConversationId = data.conversationId;
            }
            if (data.charts) {
              charts = data.charts;
              setCurrentCharts(charts);
            }
            if (data.title) {
              title = data.title;
            }
          } else if (data.type === "error") {
            // Also handle an error object if it appears as the last buffered payload.
            const streamError = new Error(
              data.message || data.error || "Stream error",
            );
            streamError.code = data.code;
            streamError.status = data.status;
            streamError.statusCode = data.statusCode;
            streamError.statusText = data.statusText;
            throw streamError;
          }

          // Extract metadata for non-response types
          if (data.answer && data.type !== "response") {
            finalAnswer = data.answer;
          }
          if (data.conversationId && data.type !== "response") {
            newConversationId = data.conversationId;
          }
          if (data.charts && data.type !== "response") {
            charts = data.charts;
            setCurrentCharts(charts);
          }
          if (data.title && data.type !== "response") {
            title = data.title;
          }
        } catch (parseError) {
          // Invalid trailing JSON in remaining buffer, ignore.
          // Re-throw non-SyntaxError exceptions so stream errors bubble up.
          if (!(parseError instanceof SyntaxError)) {
            throw parseError;
          }
        }
      }

      // If we didn't get a final answer, use what we accumulated
      if (!finalAnswer && typedResponse) {
        finalAnswer = typedResponse;
      }

      // If this is a new conversation (no existing conversationId)
      if (!conversationId && newConversationId) {
        setConversationId(newConversationId);
        setTimeoutConversationId(newConversationId);

        // Update URL to include the new conversation ID
        navigate(`/chat/${newConversationId}`, { replace: true });

        // Create new conversation entry for the list (only metadata, not full messages)
        const newConversation = {
          conversationId: newConversationId, // Keep internal structure consistent
          title: title || `Chat ${conversations.length + 1}`,
          lastUpdatedUtc: new Date().toISOString(), // Add current timestamp
        };

        setConversations((prev) => {
          const newConversations = [newConversation, ...prev]; // Add to top
          // Set the current conversation index to 0 (the newly added conversation at top)
          setCurrentConversationIndex(0);
          return newConversations;
        });
      } else if (conversationId && newConversationId) {
        // Update lastUpdatedUtc for existing conversation
        setConversations((prev) => {
          const updatedConversations = prev.map((conv) =>
            conv.conversationId === conversationId
              ? { ...conv, lastUpdatedUtc: new Date().toISOString() }
              : conv,
          );

          // Re-sort by lastUpdatedUtc (most recent first)
          return updatedConversations.sort((a, b) => {
            if (!a.lastUpdatedUtc || !b.lastUpdatedUtc) return 0;
            return new Date(b.lastUpdatedUtc) - new Date(a.lastUpdatedUtc);
          });
        });
      }

      // Process and display final answer
      const processedAnswer = processResponseText(
        finalAnswer || "Sorry, I could not process your request.",
      );
      const botMessage = createMessage(processedAnswer, "ai", charts);

      // Clear thinking messages only after we have the final answer ready
      if (thinkingMessageTimeoutRef.current) {
        clearTimeout(thinkingMessageTimeoutRef.current);
        thinkingMessageTimeoutRef.current = null;
      }
      setThinkingMessages([]);
      setIsTyping(false);
      setTypedResponse("");
      setCurrentCharts(charts);

      // Add the complete message to the messages array
      setMessages((prev) => [...prev, botMessage]);
      setTimeoutConversationId(null);
      setTimeoutNotice(null);

      // Track successful response
      // if (posthog) {
      //   posthog.capture('response_received', {
      //     distinct_id: user?.sub,
      //     email: user?.email,
      //     name: user?.name,
      //     response_length: processedAnswer.length,
      //     question_count: questionCount,
      //     response_time: Date.now(), // You could calculate actual response time
      //   });
      // }
    } catch (error) {
      console.error("Error in chat API call:", error);

      // Unified timeout handling for:
      // 1) direct HTTP timeout statuses (e.g. 504/524), and
      // 2) mid-stream/network failures after an initial 200 streaming response.
      const isCloudFrontStreamDrop =
        typeof streamEndpoint === "string" &&
        streamEndpoint.includes("/conversationstream/") &&
        error?.name === "TypeError" &&
        /network error|failed to fetch|network request failed|load failed/i.test(
          error?.message || "",
        );
      const isTimeout = isLikelyTimeoutError(error) || isCloudFrontStreamDrop;
      
      // Clear thinking messages on error
      if (thinkingMessageTimeoutRef.current) {
        clearTimeout(thinkingMessageTimeoutRef.current);
        thinkingMessageTimeoutRef.current = null;
      }
      setThinkingMessages([]);
      
      // If it's a timeout, show special message instead of generic error
      if (isTimeout) {
        if (currentConversationId) {
          setTimeoutConversationId(currentConversationId);
        }
        setTimeoutNotice(
          "This request is still running on the server and may take a few minutes to complete. Please wait 1-2 minutes, then click the Refresh button.",
        );
        setCurrentCharts([]);
        setIsTyping(false);
        setTypedResponse("");

        return; // Don't show generic error message for timeout
      }

      const errorMessage = createMessage(
        "Sorry, I encountered an error while processing your request. Please try again.",
        "ai",
        [],
      );

      // Don't add to messages yet - wait for typing to complete
      setCurrentCharts([]);
      setIsTyping(true);
      typeResponse(errorMessage.text, []);

      // Track error
      // if (posthog) {
      //   posthog.capture('api_error', {
      //     distinct_id: user?.sub,
      //     email: user?.email,
      //     name: user?.name,
      //     error_message: error.message,
      //     question_count: questionCount,
      //     question_preview: message.substring(0, 100),
      //   });
      // }
    } finally {
      setIsLoading(false);
    }
  };

  // Function to refresh/reload the current conversation
  const handleRefreshConversation = async () => {
    const refreshConversationId = conversationId || timeoutConversationId;
    if (!refreshConversationId) {
      return;
    }

    const currentHumanCount = messages.filter((m) => m.type === "human").length;

    setIsLoading(true);
    setThinkingMessages([]);
    setTypedResponse("");
    setCurrentCharts([]);

    try {
      const authHeaders = await getAuthHeaders();
      const response = await axios.get(
        `${config.apiUrl}/conversation/${refreshConversationId}`,
        {
          headers: authHeaders,
        }
      );

      let conversationData = response.data;
      if (response.data.body) {
        conversationData = response.data.body;
      } else if (response.data.data) {
        conversationData = response.data.data;
      }

      const fetchedMessages = conversationData.messages || [];
      const lastMessage = fetchedMessages[fetchedMessages.length - 1];
      const fetchedHumanCount = fetchedMessages.filter(
        (m) => m.type === "human",
      ).length;
      const hasCaughtUpToLatestQuestion = fetchedHumanCount >= currentHumanCount;
      const hasCompletedResponse =
        hasCaughtUpToLatestQuestion && lastMessage?.type === "ai";

      if (hasCompletedResponse) {
        // Response is ready: replace timeout UI with the actual conversation content.
        setTimeoutNotice(null);
        setTimeoutConversationId(null);
        setMessages(fetchedMessages);

        if (lastMessage.charts && lastMessage.charts.length > 0) {
          setCurrentCharts(lastMessage.charts);
        }
      } else {
        // Still processing: keep timeout state and allow another refresh.
        setTimeoutNotice(
          "This request is still running on the server and may take a few minutes to complete. Please wait 1-2 minutes, then click the Refresh button.",
        );
      }
    } catch (error) {
      console.error('Error refreshing conversation:', error);
      const errorMessage = createMessage("Sorry, I encountered an error while refreshing the conversation. Please try again.", "ai", []);
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const typeResponse = (response, charts = []) => {
    // Clear any existing typing interval
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
      typingIntervalRef.current = null;
    }

    // Store response and charts in refs for access in interval
    typingResponseRef.current = response;
    typingChartsRef.current = charts;
    typingStartTimeRef.current = Date.now();

    setTypedResponse(""); // Clear any existing response

    const CHAR_DELAY_MS = 20; // Delay between characters in milliseconds

    const updateTyping = () => {
      const currentResponse = typingResponseRef.current;
      const currentCharts = typingChartsRef.current;

      if (!currentResponse) {
        // Safety check - if response is cleared, stop typing
        if (typingIntervalRef.current) {
          clearInterval(typingIntervalRef.current);
          typingIntervalRef.current = null;
        }
        setIsTyping(false);
        return;
      }

      const elapsed = Date.now() - typingStartTimeRef.current;
      const targetIndex = Math.min(
        Math.floor(elapsed / CHAR_DELAY_MS),
        currentResponse.length,
      );

      if (targetIndex < currentResponse.length) {
        setTypedResponse(currentResponse.substring(0, targetIndex + 1));
      } else {
        // Typing complete
        setTypedResponse(currentResponse);
        setIsTyping(false);
        // Add the complete message to the messages array after typing is done
        setMessages((prev) => [
          ...prev,
          createMessage(currentResponse, "ai", currentCharts || []),
        ]);

        // Clear the interval
        if (typingIntervalRef.current) {
          clearInterval(typingIntervalRef.current);
          typingIntervalRef.current = null;
        }
      }
    };

    // Use a shorter interval to ensure smooth updates even when throttled
    // This will catch up when the tab becomes visible again
    typingIntervalRef.current = setInterval(updateTyping, 10);

    // Initial update
    updateTyping();
  };

  const handleSuggestionClick = (suggestion) => {
    setInputMessage(suggestion);

    // Track suggestion click
    // if (posthog) {
    //   posthog.capture('suggestion_clicked', {
    //     distinct_id: user?.sub,
    //     email: user?.email,
    //     name: user?.name,
    //     suggestion_text: suggestion,
    //     suggestion_length: suggestion.length,
    //   });
    // }
  };

  const handleConversationTitleChange = async (index, newTitle) => {
    const conv = conversations[index];
    if (!conv) return;

    // Update local state immediately for instant UI feedback
    setConversations((prev) =>
      prev.map((c, i) => (i === index ? { ...c, title: newTitle } : c)),
    );

    // Then call the API
    try {
      const authHeaders = await getAuthHeaders();
      await axios.patch(
        `${config.apiUrl}/conversation/${conv.conversationId}/title`,
        { title: newTitle },
        { headers: authHeaders },
      );
    } catch (err) {
      console.error("Error updating conversation title", err);
    }
  };

  const handleHistoryClick = (message) => {
    // Find the corresponding response in the messages array
    const messageIndex = messages.findIndex((m) => m === message);
    if (messageIndex !== -1 && messageIndex + 1 < messages.length) {
      const response = messages[messageIndex + 1];
      setCurrentCharts(response.charts || []);
      setTypedResponse(response.text); // Show full response immediately for history
    } else {
      setCurrentCharts([]);
      setTypedResponse("");
    }

    // Track history click
    // if (posthog) {
    //   posthog.capture('history_message_clicked', {
    //     distinct_id: user?.sub,
    //     email: user?.email,
    //     name: user?.name,
    //     message_index: messageIndex,
    //     total_messages: messages.length,
    //     message_sender: message.sender,
    //   });
    // }
  };

  const handleAddConversation = () => {
    if (isLoading) {
      return;
    }

    // Clear everything and reset conversation state
    setMessages([]);
    setCurrentCharts([]);
    setInputMessage("");
    setTypedResponse("");
    setThinkingMessages([]);
    setQuestionCount(0);
    setConversationId(null); // Reset conversation ID
    setTimeoutConversationId(null);
    setTimeoutNotice(null);
    setCurrentConversationIndex(-1); // Reset current conversation index

    // Navigate to /chat without conversation ID
    navigate("/chat", { replace: true });

    // Close mobile nav when new conversation is started
    closeMobileNav();

    // Track new conversation start
    if (posthog) {
      posthog.capture("new_conversation_started", {
        distinct_id: user?.sub,
        email: user?.email,
        name: user?.name,
      });
    }
  };

  const handleConversationClick = async (conversationIndex) => {
    if (isLoading) {
      return;
    }

    const conversation = conversations[conversationIndex];

    if (conversation) {
      setTimeoutConversationId(null);
      setTimeoutNotice(null);
      setConversationId(conversation.conversationId); // Use conversationId from new structure
      setCurrentConversationIndex(conversationIndex);
      setCurrentCharts([]);
      setTypedResponse("");
      setThinkingMessages([]);

      // Navigate to the conversation URL
      navigate(`/chat/${conversation.conversationId}`, { replace: true });

      // Close mobile nav when conversation is selected
      closeMobileNav();

      try {
        // Fetch conversation details from API
        const authHeaders = await getAuthHeaders();
        const response = await axios.get(
          `${config.apiUrl}/conversation/${conversation.conversationId}`, // Use conversationId
          {
            headers: authHeaders,
          },
        );

        // Extract conversation data
        let conversationData = response.data;
        if (response.data.body) {
          conversationData = response.data.body;
        } else if (response.data.data) {
          conversationData = response.data.data;
        }

        // Set messages from API response (keep original structure)
        const fetchedMessages = conversationData.messages || [];
        setMessages(fetchedMessages);

        // Don't update questionCount when loading existing conversations
        // Only increment questionCount when submitting new questions via /chat endpoint

        // Track conversation switch
        if (posthog) {
          posthog.capture("conversation_switched", {
            distinct_id: user?.sub,
            email: user?.email,
            name: user?.name,
            conversation_id: conversation.conversationId, // Use conversationId
            conversation_title: conversation.title,
            message_count: fetchedMessages.length,
          });
        }
      } catch (error) {
        console.error("Error fetching conversation details:", error);
        // Handle error - could show error message or fallback
        setMessages([]);
        setQuestionCount(0);
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (!isAuthenticated) {
    return <div>Please login to use the chat</div>;
  }

  const refreshConversationId = conversationId || timeoutConversationId;
  const humanMessageCount = messages.filter((m) => m.type === "human").length;
  const aiMessageCount = messages.filter((m) => m.type === "ai").length;
  const hasPendingQuestion = humanMessageCount > aiMessageCount;
  const hasReachedQuestionLimit =
    !isWdlUser &&
    config.limits.maxQuestions !== null &&
    questionCount >= config.limits.maxQuestions;
  const isInputDisabled = hasReachedQuestionLimit || hasPendingQuestion;
  const disabledPlaceholder = hasPendingQuestion
    ? "Please wait for the current question to be answered."
    : "Question limit reached. Please contact customer service.";

  return (
    <div className="chat-layout">
      {/* Mobile Navigation Overlay */}
      {isMobileNavOpen && (
        <div className="mobile-chat-nav-overlay" onClick={closeMobileNav}></div>
      )}

      {/* Mobile Navigation Panel */}
      {isMobileNavOpen && (
        <div
          className="mobile-chat-nav-panel"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="mobile-nav-close-button"
            onClick={closeMobileNav}
            aria-label="Close navigation"
          >
            ×
          </button>
          <ChatHistory
            messages={messages}
            onAddConversation={handleAddConversation}
            conversations={conversations}
            onConversationClick={handleConversationClick}
            onHistoryClick={handleHistoryClick}
            closeMobileNav={closeMobileNav}
            onTitleChange={handleConversationTitleChange}
            currentConversationIndex={currentConversationIndex}
            isRequestInProgress={isLoading}
          />
        </div>
      )}

      <div className="chat-content">
        <div className="desktop-chat-history">
          <ChatHistory
            messages={messages}
            onAddConversation={handleAddConversation}
            conversations={conversations}
            onConversationClick={handleConversationClick}
            currentConversationIndex={currentConversationIndex}
            isRequestInProgress={isLoading}
            onTitleChange={handleConversationTitleChange}
          />
        </div>
        <div className="chat-main">
          <div className="chat-response">
            {/* Display all messages in the conversation except the current one being typed */}
            {messages.map((message, index) => {
              const cssClass =
                message.type === "human" ? "user-question" : "response bot";
              
              return (
                <div key={index} className={cssClass}>
                  {message.text}
                  {/* Display charts for AI messages */}
                  {message.type === "ai" &&
                    message.charts &&
                    message.charts.length > 0 && (
                      <div className="message-charts">
                        {message.charts.map((chart, chartIndex) => (
                          <ChartDisplay
                            key={chartIndex}
                            chart={chart}
                            onImageLoad={() => {
                              // Scroll to bottom when chart image loads
                              setTimeout(() => {
                                messagesEndRef.current?.scrollIntoView({
                                  behavior: "smooth",
                                });
                              }, 100);
                            }}
                          />
                        ))}
                      </div>
                    )}
                </div>
              );
            })}

            {/* Timeout notice is intentionally outside chat message history */}
            <TimeoutNotice
              message={timeoutNotice}
              onRefresh={handleRefreshConversation}
              isLoading={isLoading}
              canRefresh={Boolean(refreshConversationId)}
            />

            {/* Show thinking messages - only show the latest one */}
            {thinkingMessages.length > 0 &&
              thinkingMessages[thinkingMessages.length - 1] && (
                <div className="response bot">
                  <ThinkingText
                    text={thinkingMessages[thinkingMessages.length - 1]}
                    speed="normal"
                  />
                </div>
              )}

            {/* Show loading indicator - only when loading and no thinking messages or typing */}
            {isLoading &&
              !timeoutNotice &&
              !isTyping &&
              thinkingMessages.length === 0 &&
              typedResponse === "" && (
                <div className="response bot">
                  <ThinkingText text="Thinking..." speed="normal" />
                </div>
              )}

            {/* Show typing effect for current response (only while typing) */}
            {isTyping && (
              <div className="response bot">
                {typedResponse}
                <span className="cursor">|</span>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Bottom section - fixed to bottom */}
          <div className="chat-bottom-section">
            <ChatInput
              onSubmit={handleSubmit}
              value={inputMessage}
              onChange={(value) => {
                setInputMessage(value);
              }}
              onSuggestionClick={handleSuggestionClick}
              showSuggestions={!messages.some((m) => m.type === "human")}
              disabled={isInputDisabled}
              disabledPlaceholder={disabledPlaceholder}
            />
            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
